import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOCATIONS_IMG =
  "https://images.unsplash.com/photo-1543372654-b45dd90eeee5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxnb3Zlcm5tZW50JTIwYnVpbGRpbmclMjB3YXNoaW5ndG9uJTIwZGMlMjBkYXJrfGVufDB8fHx8MTc3NjgyNDAzNnww&ixlib=rb-4.1.0&q=85";

const offices = [
  { city: "Richmond, VA", tag: "Headquarters" },
  { city: "Fairfax, VA", tag: "Office" },
  { city: "Washington, DC", tag: "Office" },
  { city: "Dover, DE", tag: "Office" },
  { city: "Charlotte, NC", tag: "Office" },
  { city: "Las Vegas, NV", tag: "Office" },
];

const services = [
  "Government Consulting",
  "Project Management",
  "Business Strategy",
  "Product Procurement",
  "Agile / Scrum",
  "Staffing Services",
  "HR Consulting",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Inquiry received. A principal will respond shortly.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#050914]"
    >
      {/* Locations strip */}
      <div
        id="locations"
        className="relative border-t border-white/10"
        data-testid="locations-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#d4af37] mb-5 reveal">
                <span className="gold-rule" /> National Reach
              </p>
              <h3 className="font-serif-editorial text-3xl md:text-5xl font-light text-white leading-[1.05] reveal">
                Six offices. <span className="italic text-[#d4af37]">One firm.</span>
              </h3>
            </div>
            <p className="text-slate-400 max-w-md reveal">
              Headquartered in Richmond, VA with satellite offices positioned for
              federal, regional, and enterprise engagements nationwide.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 border border-white/10">
            {offices.map((o) => (
              <div
                key={o.city}
                data-testid={`office-${o.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-[#0a1324] p-5 md:p-6"
              >
                <MapPin
                  size={18}
                  strokeWidth={1.2}
                  className="text-[#d4af37] mb-3"
                />
                <p className="font-serif-editorial text-white text-lg leading-tight">
                  {o.city}
                </p>
                <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mt-1">
                  {o.tag}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact split */}
      <div className="relative border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-0 md:px-0 grid grid-cols-1 lg:grid-cols-2">
          {/* Left image + statement */}
          <div className="relative min-h-[420px] lg:min-h-full">
            <img
              src={LOCATIONS_IMG}
              alt="U.S. Capitol at night"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#050914]/95 via-[#050914]/70 to-[#050914]/30" />
            <div className="relative z-10 p-8 md:p-16 h-full flex flex-col justify-between min-h-[420px]">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#d4af37] mb-5">
                  <span className="gold-rule" /> Engage the Firm
                </p>
                <h2 className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] text-white max-w-md">
                  Begin a <span className="italic text-[#d4af37]">conversation</span>.
                </h2>
                <p className="mt-6 text-slate-300 max-w-md leading-relaxed">
                  Share a brief on your mandate and a principal from Richmond Holdings
                  Group will respond within one business day.
                </p>
              </div>
              <div className="mt-10 flex items-center gap-3 text-slate-300">
                <Mail size={18} className="text-[#d4af37]" />
                <a
                  href="mailto:contact@richmondhg.com"
                  data-testid="contact-email-link"
                  className="link-gold text-sm tracking-wide"
                >
                  contact@richmondhg.com
                </a>
              </div>
            </div>
          </div>

          {/* Right form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="p-8 md:p-16 bg-[#070d1a] border-t lg:border-t-0 lg:border-l border-white/10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.26em] text-slate-500">
                  Full Name
                </span>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  data-testid="contact-input-name"
                  className="rhg-input mt-2 w-full px-4 py-3.5 text-sm"
                  placeholder="Your full name"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.26em] text-slate-500">
                  Email
                </span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  data-testid="contact-input-email"
                  className="rhg-input mt-2 w-full px-4 py-3.5 text-sm"
                  placeholder="you@agency.gov"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.26em] text-slate-500">
                  Company / Agency
                </span>
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  data-testid="contact-input-company"
                  className="rhg-input mt-2 w-full px-4 py-3.5 text-sm"
                  placeholder="Organization"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.26em] text-slate-500">
                  Service of Interest
                </span>
                <select
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  data-testid="contact-input-service"
                  className="rhg-input mt-2 w-full px-4 py-3.5 text-sm appearance-none"
                >
                  <option value="">Select a service</option>
                  {services.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="block mt-5">
              <span className="text-[10px] uppercase tracking-[0.26em] text-slate-500">
                Project Brief
              </span>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={5}
                data-testid="contact-input-message"
                className="rhg-input mt-2 w-full px-4 py-3.5 text-sm resize-none"
                placeholder="Tell us about your mandate, timelines, and desired outcome."
              />
            </label>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="group mt-8 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#d4af37] hover:bg-[#f1d570] disabled:opacity-60 disabled:cursor-not-allowed text-[#050914] px-8 py-5 text-[12px] font-bold uppercase tracking-[0.28em] transition-colors"
            >
              {loading ? "Submitting…" : "Submit Inquiry"}
              {!loading && (
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              )}
            </button>
            <p className="mt-5 text-[11px] text-slate-500 leading-relaxed">
              By submitting you agree to be contacted by Richmond Holdings Group
              regarding your inquiry. We respect your privacy.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
